import React, { createContext, useContext, useEffect } from "react";
import { FormData } from "../interfaces/IForms";
import { Participant, ParticipantWithId } from "../interfaces/IParticipants";
import { useParticipants } from "../hooks/useParticipants";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  participant?: ParticipantWithId;
}

interface ParticipantsContextType {
  // Stan
  participants: ParticipantWithId[]; // Tylko aktywni zawodnicy (filtrowane w Firestore)
  isLoading: boolean;

  // Orkiestrowane metody (z hooka)
  handleParticipantAdd: (participantData: Participant) => Promise<OperationResult>;
  handleParticipantAddFromForm: (formData: FormData) => Promise<OperationResult>;
  handleParticipantUpdate: (
    participantId: string,
    updatedData: Partial<ParticipantWithId>
  ) => Promise<OperationResult>;
  handleParticipantDelete: (participantId: string) => Promise<OperationResult>;
  refreshParticipants: () => Promise<void>;
}

const ParticipantsContext = createContext<ParticipantsContextType | undefined>(undefined);

export default function ParticipantsProvider({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId: string;
}) {
  // Hook robi całą robotę - DESTRUKTURYZACJA
  const {
    // Stan
    participants,
    isLoading,

    // Orkiestrowane metody
    handleParticipantAdd,
    handleParticipantAddFromForm,
    handleParticipantUpdate,
    handleParticipantDelete,
    refreshParticipants,
  } = useParticipants(userId);

  // Walidacja spójności: sprawdź czy zawodnicy należą do aktualnego użytkownika
  useEffect(() => {
    if (participants.length > 0) {
      const invalidParticipants = participants.filter(
        (participant) => participant.userId !== userId
      );
      if (invalidParticipants.length > 0) {
        console.warn(
          `🚨 [ParticipantsContext] Niekonsystencja danych: znaleziono ${invalidParticipants.length} zawodników należących do innych użytkowników`
        );
        console.warn(
          "Nieprawidłowi zawodnicy:",
          invalidParticipants.map((p) => ({ id: p.id, userId: p.userId, expectedUserId: userId }))
        );
      }
    }
  }, [participants, userId]);

  return (
    <ParticipantsContext.Provider
      value={{
        // Stan
        participants,
        isLoading,

        // Orkiestrowane metody
        handleParticipantAdd,
        handleParticipantAddFromForm,
        handleParticipantUpdate,
        handleParticipantDelete,
        refreshParticipants,
      }}
    >
      {children}
    </ParticipantsContext.Provider>
  );
}

export const useParticipantsContext = (): ParticipantsContextType => {
  const context = useContext(ParticipantsContext);
  if (context === undefined) {
    throw new Error(
      "Błąd w ParticipantsContext: useParticipantsContext must be used within an ParticipantsProvider"
    );
  }
  return context;
};
